<template>
  <div class="operator-form">
    <div class="operator-form-header">
      <drawer-dialog-controls
        :is-pristine="isPristine"
        :is-loading="isLoading"
        :is-disabled="!isFormValid"
        @close="$emit('close')"
        @save="saveWithTrim"
      >
        {{ dialogTitle }}
      </drawer-dialog-controls>
    </div>

    <div class="operator-form-content">
      <v-form v-model="isFormValid">
        <v-card>
          <v-card-title>
            Security settings
          </v-card-title>
          <v-divider />

          <v-container>
            <v-row class="mx-0">
              <v-col cols="6" class="pb-0">
                <v-row class="mx-0">
                  <v-text-field
                    v-if="!localModelValue.user_id"
                    v-model="localModelValue.login"
                    label="Login"
                    placeholder="Login"
                  />
                  <template v-else>
                    <div class="login-text">
                      <label class="v-label v-label--active theme--light">Login</label>
                      <div>{{ localModelValue.login }}</div>
                    </div>
                  </template>
                  <v-switch
                    v-model="localModelValue.two_fa"
                    class="ml-3"
                    :readonly="!localModelValue.two_fa"
                    label="2FA"
                    hide-details
                  />
                </v-row>
              </v-col>
              <v-col cols="6" class="pb-0">
                <v-text-field
                  v-model="localModelValue.password"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  append-outer-icon="mdi-autorenew"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  hint="At least 8 characters"
                  counter
                  @click:append-outer="generatePassword"
                  @click:append="showPassword = !showPassword"
                />
              </v-col>
            </v-row>
            <v-row class="mx-0">
              <v-col cols="6" class="pt-0">
                <v-row class="mx-0">
                  <v-text-field v-model="localModelValue.email" label="Email" placeholder="Email" :readonly="readonly" />
                  <v-checkbox v-model="localModelValue.email_verification" class="ml-3" label="Email verification" />
                </v-row>
                <v-text-field v-model="localModelValue.name" label="Name" placeholder="Name" :readonly="readonly" />

                <v-textarea
                  v-model="localModelValue.ip"
                  label="IP"
                  hint="Use comma as delimiter, to allow all use *"
                  persistent-hint
                  :readonly="readonly"
                  rows="1"
                />
              </v-col>
              <v-col cols="6" class="pt-0">
                <v-row class="mx-0">
                  <v-text-field v-model="localModelValue.phone" label="Phone" placeholder="Phone" :readonly="readonly" />
                  <v-checkbox v-model="localModelValue.phone_verification" class="ml-3" label="Phone verification" />
                </v-row>
                <v-select
                  v-model="localModelValue.status"
                  :items="statusOptions"
                  label="Status"
                />

                <v-select
                  v-model="localModelValue.role_id"
                  :items="Object.values(rolesById)"
                  :return-object="false"
                  :readonly="readonly"
                  label="Role"
                  item-value="role_id"
                  item-text="name"
                  hide-details
                  clearable
                  @change="onRoleChanged"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <v-card
          v-if="canEditTransactionLimits && localModelValue.user_id"
          class="mt-3"
        >
          <v-card-title>
            Manual transaction limits
          </v-card-title>
          <v-divider />

          <v-progress-circular v-if="isLoadingLimits" indeterminate />
          <operator-limits
            v-else
            :transaction-limits="transactionLimits"
            :is-loading="isLoadingLimits"
            :expenditure-by-type="expenditureByType"
            @save="$emit('save-limit', $event)"
            @delete="$emit('delete-limit', $event)"
          />
        </v-card>

        <v-card
          v-if="canEditPayoutLimits"
          :loading="isLoadingPayoutStats"
          class="mt-3"
        >
          <v-card-title>
            Payout limits
          </v-card-title>
          <v-divider />
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    Daily Limit
                  </th>
                  <th class="text-left">
                    Weekly Limit
                  </th>
                  <th class="text-left">
                    Monthly Limit
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <v-text-field
                      v-model.number="localModelValue.payout_limits.daily_limit"
                      :prefix="`${payoutStats.today} € /`"
                      :loading="isLoadingPayoutStats"
                      class="mt-0 pt-0"
                      type="number"
                      min="0"
                      hide-details
                      suffix="€"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="localModelValue.payout_limits.weekly_limit"
                      :prefix="`${payoutStats.last_week} € /`"
                      :loading="isLoadingPayoutStats"
                      class="mt-0 pt-0"
                      type="number"
                      min="0"
                      hide-details
                      suffix="€"
                      />
                    </td>
                    <td>
                    <v-text-field
                      v-model.number="localModelValue.payout_limits.monthly_limit"
                      :prefix="`${payoutStats.last_month} € /`"
                      :loading="isLoadingPayoutStats"
                      class="mt-0 pt-0"
                      type="number"
                      min="0"
                      hide-details
                      suffix="€"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>
            Project settings
          </v-card-title>
          <v-divider />

          <v-container>
            <v-row class="mx-0">
              <v-col cols="12">
                <v-select
                  v-model="localModelValue.entities"
                  :items="Object.values(entitiesById)"
                  :error="!isPristine && !localModelValue.entities?.length"
                  :rules="[v => !!v.length || 'This field is required']"
                  :return-object="false"
                  :readonly="readonly"
                  label="Projects"
                  multiple
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>Reports</v-card-title>
          <v-divider />

          <v-container>
            <v-row class="mx-0">
              <v-col cols="6">
                <v-select
                  v-model="localModelValue.daily_report"
                  :items="Object.values(entitiesById)"
                  :disabled="localModelValue.daily_report_all"
                  :readonly="readonly"
                  label="Daily report mails"
                  multiple
                  hide-details
                />
                <v-row>
                  <v-col
                    class="pb-0"
                    cols="6"
                  >
                    <v-checkbox
                      v-model="localModelValue.daily_report_all"
                      :readonly="readonly"
                      class="mt-0"
                      hide-details
                      label="All projects"
                    />
                  </v-col>
                  <v-col
                    class="pb-0"
                    cols="6"
                  >
                    <v-checkbox
                      v-model="localModelValue.daily_report_totals_only"
                      :readonly="readonly"
                      class="mt-0"
                      hide-details
                      label="Totals only"
                    />
                  </v-col>
                </v-row>

                <v-card
                  v-if="permissionsByGroup[REPORT_GROUP_KEY]"
                  class="mt-6"
                >
                  <v-card-title class="permission-group-title">
                    Permissions
                    <v-checkbox
                      :input-value="getSelectAllValue(REPORT_GROUP_KEY)"
                      :indeterminate="getIsIndeterminate(REPORT_GROUP_KEY)"
                      class="ma-0"
                      hide-details
                      label="Select All"
                      :readonly="readonly"
                      @change="toggleGroup(REPORT_GROUP_KEY, $event)"
                    />
                  </v-card-title>
                  <v-divider />

                  <v-row class="ma-0 pa-3">
                    <v-checkbox
                      v-for="permission in permissionsByGroup[REPORT_GROUP_KEY]"
                      v-if="permission.type === 'boolean'"
                      v-model="localModelValue.permission[permission.key]"
                      style="flex:50%;"
                      class="mt-1"
                      hide-details
                      :key="permission.key"
                      :readonly="readonly"
                      :label="permission.name"
                    />
                  </v-row>
                </v-card>
              </v-col>

              <v-col cols="6"
                v-for="permission in permissionsByGroup[REPORT_GROUP_KEY]"
                v-if="permission.type === 'multiple' && permission.list.length"
                :key="permission.key"
              >
                <v-select
                  v-model="localModelValue.permission[permission.key]"
                  :items="permission.list"
                  :readonly="readonly"
                  :label="permission.name"
                  :placeholder="permission.name"
                  multiple
                  hide-details
                />
              </v-col>

            </v-row>
          </v-container>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>Roles and Permissions</v-card-title>
          <v-divider />

          <v-container>
            <div class="permissions pa-0">
              <v-card
                class="mb-4 permission-card"
                v-for="(permissions, permissionGroup) in permissionsByGroup"
                v-if="permissionGroup !== REPORT_GROUP_KEY && permissions.length"
                :key="permissionGroup"
              >
                <v-card-title class="permission-group-title">
                  {{ capitalize(permissionGroup) }}
                  <v-checkbox
                    :input-value="getSelectAllValue(permissionGroup)"
                    :indeterminate="getIsIndeterminate(permissionGroup)"
                    class="ma-0"
                    hide-details
                    label="Select All"
                    :readonly="readonly"
                    @change="toggleGroup(permissionGroup, $event)"
                  />
                </v-card-title>
                <v-divider />

                <v-row class="ma-0 pa-3">
                  <v-checkbox
                    v-for="permission in permissions"
                    v-if="permission.type === 'boolean'"
                    v-model="localModelValue.permission[permission.key]"
                    style="flex:50%;"
                    class="mt-1"
                    hide-details
                    :key="permission.key"
                    :readonly="readonly"
                    :label="permission.name"
                  />
                </v-row>
                <v-row class="mx-0 px-4">
                  <v-select
                    v-for="permission in permissions"
                    v-if="permission.type === 'multiple'"
                    v-model="localModelValue.permission[permission.key]"
                    :items="permission.list"
                    multiple
                    :readonly="readonly"
                    :label="permission.name"
                    :placeholder="permission.name"
                    :key="permission.key"
                    hide-details
                    class="pb-3"
                    chips
                    small-chips
                  />
                </v-row>
              </v-card>
            </div>
          </v-container>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>System Notifications</v-card-title>
          <v-divider />
          <v-container>
            <v-row>
              <v-col cols="3">
                <v-checkbox
                  v-model="localModelValue.notifications.chargebacks"
                  class="mt-0"
                  label="Chargebacks"
                  hide-details
                />
                <v-checkbox
                  v-model="localModelValue.notifications.large_transactions"
                  class="mt-1"
                  label="Large Transactions"
                  hide-details
                />
                <v-checkbox
                  v-model="localModelValue.notifications.cms_deploy"
                  class="mt-1"
                  label="CMS Deploy"
                  hide-details
                />
                <v-checkbox
                  v-model="localModelValue.notifications.payout_limits"
                  class="mt-1"
                  label="Payout Limits"
                  hide-details
                />
              </v-col>
              <v-col cols="3">
                <v-checkbox
                  v-model="localModelValue.notifications.game_import"
                  class="mt-0"
                  label="Game Import"
                  hide-details
                />
                <v-checkbox
                  v-model="localModelValue.notifications.negative_ggr"
                  class="mt-0"
                  label="Negative GGR Alerts"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>Kiosk Settings (Optional)</v-card-title>
          <v-divider />

          <v-container>
            <v-row>
              <v-col cols="4">
                <v-select
                  v-model="localModelValue.base"
                  :items="Object.values(entitiesById)"
                  hide-details
                  :return-object="false"
                  label="Base Project"
                  :clearable="readonly"
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="localModelValue.base_account_id"
                  hide-details
                  label="Base Account ID"
                  :readonly="readonly"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-form>
    </div>
  </div>
</template>

<script>
  import { cloneDeep, capitalize, isArray } from 'lodash'
  import OperatorLimits from './OperatorLimits.vue'
  import { DrawerDialogControls } from '../../../components/core/DrawerDialog'

  const FIELDS_TO_TRIM = [ 'email', 'name', 'phone', 'password', 'login' ]

  export default {
    props: {
      operator: {
        type: Object,
        default: () => ({})
      },
      entitiesById: {
        type: Object,
        default: () => ({})
      },
      rolesById: {
        type: Object,
        default: () => ({})
      },
      permissionsByGroup: {
        type: Object,
        default: () => ({})
      },
      transactionLimits: {
        type: Array,
        default: () => ([])
      },
      expenditureByType: {
        type: Object,
        default: () => ({})
      },
      payoutStats: {
        type: Object,
        default: () => ({})
      },
      canEditTransactionLimits: Boolean,
      canEditPayoutLimits: Boolean,
      readonly: Boolean,
      isLoading: Boolean,
      isLoadingLimits: Boolean,
      isLoadingPayoutStats: Boolean,
    },
    components: {
      DrawerDialogControls,
      OperatorLimits
    },
    data () {
      return {
        localModelValue: {},
        showPassword: false,
        isPristine: true,
        isFormValid: false,
        statusOptions: [ 'active', 'blocked', 'frozen' ],
        REPORT_GROUP_KEY: 'reports'
      }
    },
    created () {
      this.localModelValue = cloneDeep(this.operator)
      for (const group in this.permissionsByGroup) {
        for (const permission of this.permissionsByGroup[group]) {
          const { key, type } = permission
          if (!(key in this.localModelValue.permission)) {
            this.$set(this.localModelValue.permission, key, type === 'boolean' ? false : [])
          }
        }
      }

      if (!isArray(this.localModelValue.permission.reports_allowed)) {
        this.localModelValue.permission.reports_allowed = []
      }

      const unwatch = this.$watch('localModelValue', () => {
        this.isPristine = false
        unwatch()
      }, { deep: true })
    },
    computed: {
      dialogTitle () {
        return this.localModelValue.user_id ? `Edit operator ${this.localModelValue.user_id}: ${this.localModelValue.login}` : 'Add new operator'
      }
    },
    methods: {
      capitalize,
      getSelectAllValue (group) {
        return this.permissionsByGroup[group].every(({ key, type }) => {
          return type !== 'boolean' || this.localModelValue.permission[key]
        })
      },
      getIsIndeterminate (group) {
        const boolPermissions = this.permissionsByGroup[group].filter(({type}) => type === 'boolean')
        const enabledBoolPermissions = boolPermissions.filter(({key}) => this.localModelValue.permission[key])

        return !!enabledBoolPermissions.length && enabledBoolPermissions.length !== boolPermissions.length
      },
      toggleGroup (group, value) {
        for (const { key, type } of this.permissionsByGroup[group]) {
          if (type === 'boolean') {
            this.localModelValue.permission[key] = !!value
          }
        }
      },
      generatePassword () {
        this.localModelValue.password = (Math.random().toString(36) + '00000000000000000').slice(2, 8).toUpperCase() + (Math.random().toString(36) + '00000000000000000').slice(2, 8)
      },

      onRoleChanged (roleId) {
        if (!roleId) {
          return
        }

        const role = this.rolesById[roleId]
        for (const permission in this.localModelValue.permission) {
          this.$set(this.localModelValue.permission, permission, role.permissions[permission] || false)
        }

        if (Array.isArray(role.entities) && role.entities.length) {
          this.$set(this.localModelValue, 'entities', [...role.entities])
        }
      },

      saveWithTrim() {
        for (const field of FIELDS_TO_TRIM) {
          if (this.localModelValue[field]) {
            this.localModelValue[field] = this.localModelValue[field].trim()
          }
        }

        this.$emit('save', this.localModelValue)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .operator-form {
    border-radius: 0 !important;

    .login-text {
      flex: 1;
      margin-top: -7px;

      .v-label {
        zoom: 0.75;
        position: relative;
        bottom: -6px;
      }

      > div {
        line-height: 31px;
        border-style: solid;
        border-width:  0 0 thin 0;
        border-color: rgba(0, 0, 0, 0.42);
      }

    }

    &-header {
      position: sticky;
      top: 0;
      left: 0;
      z-index: 1;
    }

    &-content {
      background-color: #F2F2F2;
      padding: 1rem;

      .permissions {

        columns: 2;

        //padding: 1rem;
        //gap: 1rem;

        .permission-card {
          break-inside: avoid-column;
          //width: calc(50% - 1rem);
        }
      }
    }

    .permission-group-title {
      padding-top: 10px;
      padding-bottom: 10px;
      justify-content: space-between;
    }
  }
</style>
